import { forwardRef, isValidElement, type ReactNode, Children } from 'react';

import { DataTestId } from '../../../core/types/data-props.js';
import { StylingProps } from '../../../core/types/styling-props.js';
import { WithChildren } from '../../../core/types/with-children.js';
import { Menu } from '../../../navigation/menu/Menu.js';
import { Flex } from '../../flex/Flex.js';
import { ACTION_GROUP_GAP } from './constants.js';
import { useActionGroupContext } from './hooks/useActionGroupContext.js';

// #region Typings

/**
 * @public
 */
export interface GroupProps extends WithChildren, StylingProps, DataTestId {}

type SlotsType = {
  items: ReactNode[];
};
// #endregion

/**
 * A group of action items.
 * A horizontal / vertical divider will be rendered between grouped actions.
 * @public
 */
export const Group = forwardRef<HTMLDivElement, GroupProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: GroupProps = props;

    const slots: SlotsType = {
      items: [],
    };

    for (const child of Children.toArray(children)) {
      if (isValidElement(child)) {
        slots.items?.push(child);
      }
    }

    const { isCollapsed } = useActionGroupContext();

    return isCollapsed ? (
      <Menu.Group
        ref={forwardedRef}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
      >
        {slots.items}
      </Menu.Group>
    ) : (
      <Flex
        ref={forwardedRef}
        gap={ACTION_GROUP_GAP}
        alignItems="center"
        role="group"
        className={consumerClassName}
        style={{ ...consumerStyle, flexShrink: 0 }}
        data-testid={dataTestId}
        {...remainingProps}
      >
        {slots.items}
      </Flex>
    );
  },
);

(Group as typeof Group & { displayName: string }).displayName =
  'ActionGroup.Group';
